// 세리사무소 포털 홈 — 「내 일정」 위젯.
//
// 오늘부터 7일을 날짜 칸으로 펼쳐 내가 맡은 일(프로젝트 작업·내 업무)·휴가·출장·근태 확인을 한눈에 본다.
// 메뉴나 별도 화면이 아니라 홈 본문의 카드다. 읽기 전용이고, 본인 사번으로만 각 모듈의 조회 API 를 부른다(pages/Schedule/useSchedule.ts).
// 한 모듈이 안 떠 있거나 실패해도 나머지 일정은 그대로 보인다. 일정을 누르면 카드 안에 상세가 열리고, 「원래 화면에서 열기」로 원 모듈에 간다.
import React, { useMemo, useState } from 'react';
import { navigate } from '../../lib/navigation';
import { useModuleRegistry } from '../../lib/moduleRegistryStore';
import { useT } from '../../lib/locale';
import type { MessageKey } from '../../lib/locale.messages';
import { addDays, filterBySource, overlaps } from '../../lib/schedule';
import type { ScheduleItem, ScheduleSource, ScheduleTone } from '../../lib/schedule';
import { SOURCES, SOURCE_MODULE, useSchedule } from '../Schedule/useSchedule';
import styles from './Home.zeirishi.schedule.module.css';

const DAYS = 7;
const WEEKDAY: Record<string, string[]> = {
  ko: ['일', '월', '화', '수', '목', '금', '토'],
  ja: ['日', '月', '火', '水', '木', '金', '土'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};

const TONE_CLASS: Record<ScheduleTone, string> = {
  accent: styles.tAccent, ok: styles.tOk, warn: styles.tWarn, danger: styles.tDanger, violet: styles.tViolet, gray: styles.tGray,
};

const SOURCE_DOT: Record<ScheduleSource, string> = {
  'pjt-task': '#0071e3', 'pjt-todo': '#8e44cf', 'hr-leave': '#248a3d', 'ops-trip': '#0891b2', 'hr-attend': '#c04f00',
};

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' 의 요일(0=일) — 로컬 시간대와 무관하게 UTC 날짜로 계산한다 */
function weekdayOf(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function dayOf(it: ScheduleItem): { from: string; to: string } {
  const from = it.start.slice(0, 10);
  const to = it.allDay ? (it.end ? it.end.slice(0, 10) : from) : from;
  return { from, to };
}

function periodText(it: ScheduleItem): string {
  if (!it.allDay) return `${it.start.slice(0, 10)} ${it.start.slice(11, 16)}${it.end ? `–${it.end.slice(11, 16)}` : ''}`;
  return it.end && it.end !== it.start ? `${it.start} ~ ${it.end}` : it.start;
}

export default function ZeirishiScheduleWidget() {
  const { lang, t } = useT();
  const registry = useModuleRegistry();
  const today = todayLocal();
  const last = addDays(today, DAYS - 1);
  const state = useSchedule(today, last);

  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const shown = useMemo(() => filterBySource(state.items, enabled), [state.items, enabled]);
  const days = useMemo(() => {
    const out: { date: string; label: string; wd: number; items: ScheduleItem[] }[] = [];
    for (let i = 0; i < DAYS; i++) {
      const date = addDays(today, i);
      const wd = weekdayOf(date);
      out.push({
        date,
        wd,
        label: i === 0 ? t('schedule.widget.today') : `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`,
        items: shown.filter(it => { const r = dayOf(it); return overlaps(r.from, r.to, date, date); }),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, today, lang]);

  const selected = selectedId ? shown.find(i => i.id === selectedId) ?? null : null;
  const total = shown.length;
  const modName = (cd: string) => t(`schedule.mod.${cd}` as MessageKey);
  const srcName = (s: ScheduleSource) => t(`schedule.src.${s}` as MessageKey);
  const uniq = (xs: string[]) => Array.from(new Set(xs)).join(', ');

  const openOriginal = (it: ScheduleItem) => {
    const entry = registry.find(e => e.moduleCd === it.moduleCd);
    navigate(entry ? entry.pathPrefix : `/${it.moduleCd}`);
  };

  return (
    <section className={styles.wrap} aria-label={t('schedule.title')}>
      <div className={styles.card}>
        <div className={styles.head}>
          <div className={styles.ttl}>
            <h2>{t('schedule.title')}</h2>
            <span className={styles.sub}>
              {t('schedule.widget.sub')}{state.loaded ? ` · ${t('schedule.widget.count', { n: total })}` : ''}
            </span>
          </div>
          <div className={styles.chips} role="group" aria-label={t('schedule.filter')}>
            {SOURCES.map(s => {
              const on = enabled[s] !== false;
              const gone = state.failed.indexOf(s) >= 0 || state.missing.indexOf(s) >= 0;
              return (
                <button key={s} type="button" aria-pressed={on} disabled={gone}
                  className={`${styles.chip} ${on ? styles.chipOn : ''}`}
                  onClick={() => setEnabled(prev => ({ ...prev, [s]: prev[s] === false }))}>
                  <i style={{ background: SOURCE_DOT[s] }} aria-hidden="true" />{srcName(s)}
                </button>
              );
            })}
            <button type="button" className={styles.reload} onClick={state.reload} disabled={state.loading}
              title={t('schedule.retry')} aria-label={t('schedule.retry')}>↻</button>
          </div>
        </div>

        {state.noUser && <div className={styles.note} role="alert">{t('schedule.noUser')}</div>}
        {state.failed.length > 0 && (
          <div className={`${styles.note} ${styles.noteErr}`} role="alert">
            {t('schedule.failed', { names: uniq(state.failed.map(srcName)) })}
          </div>
        )}
        {state.missing.length > 0 && (
          <div className={styles.note} role="status">
            {t('schedule.notInstalled', { names: uniq(state.missing.map(s => modName(SOURCE_MODULE[s]))) })}
          </div>
        )}

        <div className={`${styles.week} ${state.loading ? styles.weekBusy : ''}`}>
          {days.map(d => (
            <div key={d.date} className={`${styles.day} ${d.date === today ? styles.dayToday : ''}`}>
              <div className={`${styles.dh} ${d.wd === 0 ? styles.sun : d.wd === 6 ? styles.sat : ''}`}>
                <b>{d.label}</b><span>{WEEKDAY[lang][d.wd]}</span>
              </div>
              <div className={styles.list}>
                {d.items.length === 0
                  ? <span className={styles.none}>{state.loaded ? t('schedule.widget.none') : ''}</span>
                  : d.items.map(it => (
                    <button key={it.id} type="button" title={it.title}
                      className={`${styles.ev} ${TONE_CLASS[it.tone]} ${selectedId === it.id ? styles.evOn : ''}`}
                      onClick={() => setSelectedId(selectedId === it.id ? null : it.id)}>
                      {!it.allDay && <em>{it.start.slice(11, 16)}</em>}
                      <span>{it.title}</span>
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <div className={styles.detail}>
            <span className={styles.dsrc}><i style={{ background: SOURCE_DOT[selected.source] }} aria-hidden="true" />{srcName(selected.source)}</span>
            <b className={styles.dtt}>{selected.title}</b>
            <span className={styles.dmeta}>
              {periodText(selected)}
              {selected.statusNm ? ` · ${selected.statusNm}` : ''}
              {selected.location ? ` · ${selected.location}` : ''}
            </span>
            <button type="button" className={styles.dopen} onClick={() => openOriginal(selected)}>{t('schedule.detail.open')}</button>
            <button type="button" className={styles.dx} onClick={() => setSelectedId(null)} aria-label={t('schedule.detail.close')}>✕</button>
          </div>
        )}
      </div>
    </section>
  );
}
